import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button } from "@material-ui/core";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import {
    createDefaultRuleDefaultFormValues,
    CreateEmailSchedulerRuleForm,
    emailConfigurationSchema,
} from "validation/components/EmailScheduler/emailSchedulerFormRules";
import { GeneralSection } from "pages/CreateSchedulerRule/components/GeneralSection/GeneralSection";
import { ActionSection } from "pages/CreateSchedulerRule/components/ActionSection/ActionSection";
import { ExceptionSection } from "pages/CreateSchedulerRule/components/ExceptionSection/ExceptionSection";
import { Link } from "components/Typography/Typography";
import { pathName } from "pages/routes";
import {
    cancelSchedulerRuleUpdate,
    createSchedulerRule,
    fetchSchedulerDefinitions,
    updateSchedulerRule,
} from "store/features/emailScheduler/definition/definition.actions";
import {
    getEmailSchedulerDefinitionProcessingState,
    getRuleEditProperties,
} from "store/features/emailScheduler/definition/definition.selectors";
import { useProcessingStatus } from "hooks";
import { useSchedulerFormReset } from "pages/CreateSchedulerRule/hooks/useSchedulerFormReset";
import { finishFormSubmit } from "store/features/general/general.actions";
import { FilterSection } from "components/FilterSection/FilterSection";
import { getFiltersFields } from "store/features/filters";
import { useSubmitStatus } from "hooks/useSubmitStatus";

export const CreateSchedulerRule = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const definitions = useSelector(getFiltersFields);
    const { isProcessing, isSubmitted, hasError } = useSelector(getEmailSchedulerDefinitionProcessingState);
    const { isEdited, ruleId, isCopy } = useSelector(getRuleEditProperties);
    const formValues = useSchedulerFormReset();
    const methods = useForm({
        resolver: yupResolver(emailConfigurationSchema),
        defaultValues: createDefaultRuleDefaultFormValues,
    });
    useProcessingStatus(isProcessing, hasError);
    useSubmitStatus({ isSubmitted, message: "Rule successfully saved", redirectTo: pathName.SCHEDULER_OVERVIEW }, () =>
        dispatch(finishFormSubmit())
    );

    const onSubmit = (ruleForm: CreateEmailSchedulerRuleForm) => {
        const rule: CreateEmailSchedulerRuleForm = {
            ...ruleForm,
            general: {
                ...ruleForm.general,
                // send only checked exceptions
                exceptions: ruleForm.general.exceptions.filter(({ isChecked }) => isChecked),
            },
        };

        if (isEdited && !isCopy) {
            // id is read only and is available only on edit existing rule
            dispatch(updateSchedulerRule.request({ ...rule, id: ruleId }));

            return;
        }

        dispatch(createSchedulerRule.request(rule));
    };

    useEffect(() => {
        if (isCopy || isEdited) {
            methods.reset(formValues);
        }
        // eslint-disable-next-line
    }, [methods.reset, isEdited, isCopy]);

    useEffect(() => {
        if (!definitions.length) {
            dispatch(fetchSchedulerDefinitions.request());
        }

        return () => {
            if (isEdited) {
                dispatch(cancelSchedulerRuleUpdate());
            }
        };
        // eslint-disable-next-line
    }, []);

    return (
        <Box pt={3} pb={3}>
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <GeneralSection />
                    <FilterSection source="notification/autocomplete" />
                    <ActionSection />
                    <ExceptionSection />
                    <Box display="flex">
                        <Link to={pathName.SCHEDULER_OVERVIEW} pr={3}>
                            <Button color="primary" variant="contained">
                                {t("buttons.label.cancel")}
                            </Button>
                        </Link>
                        <Button type="submit" color="primary" variant="contained">
                            {t("buttons.label.submit")}
                        </Button>
                    </Box>
                </form>
            </FormProvider>
        </Box>
    );
};
